import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/i18n"; // bootstraps i18next before any component reads t()
import App from "@/App";

// Suppress the benign "ResizeObserver loop completed with undelivered
// notifications" warning that Radix's Popover/Select emits when it opens
// inside a Dialog. It's caught by CRA's error overlay in development which
// makes it look like a fatal error — it isn't. Chromium fires it whenever
// a ResizeObserver callback triggers another reflow in the same frame.
if (typeof window !== "undefined") {
  const RO_MSG = "ResizeObserver loop";
  const isRO = (e) => {
    const m = (e?.message || e?.reason?.message || "") + "";
    return m.includes(RO_MSG);
  };
  // Browsers strip cross-origin script errors down to the literal text
  // "Script error." with no filename/line/stack. It usually comes from a
  // third-party CDN'd script and is NOT actionable — surfacing a red
  // banner for it just spooks the user (they see it whenever they
  // interact with the share dialog on iOS Safari, etc.). Suppress it.
  const isCrossOriginNoise = (e) => {
    const m = ((e?.error?.message || e?.message || e?.reason?.message || "") + "").trim();
    return m === "Script error." || m === "Script error" || m === "";
  };
  window.addEventListener("error", (e) => {
    if (isRO(e) || isCrossOriginNoise(e)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return;
    }
    // Surface uncaught errors on-screen for non-technical users. If we get
    // here, either React's ErrorBoundary missed it (async code) or it
    // bubbled out entirely. Better a red banner than a white screen.
    showCrashBanner(e?.error?.message || e?.message || "Unknown error");
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    if (isRO(e) || isCrossOriginNoise(e)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return;
    }
    showCrashBanner(e?.reason?.message || String(e?.reason || "Unknown error"));
  }, true);

  // Persistent, dismissible on-screen crash banner — the only visible clue
  // when a user hits a white-screen bug and can't open DevTools.
  function showCrashBanner(msg) {
    try {
      const id = "__hepcure_crash_banner__";
      if (document.getElementById(id)) return; // one banner at a time
      const el = document.createElement("div");
      el.id = id;
      el.style.cssText = [
        "position:fixed", "top:0", "left:0", "right:0", "z-index:2147483647",
        "background:#B4351B", "color:white",
        "padding:12px 16px 14px",
        "font:600 13px/1.4 system-ui,-apple-system,'Segoe UI',sans-serif",
        "box-shadow:0 4px 18px rgba(0,0,0,0.35)",
      ].join(";");
      el.innerHTML =
        '<div style="max-width:900px;margin:0 auto">' +
        '<div style="font-weight:800;margin-bottom:4px">Something went wrong</div>' +
        '<div style="font:400 12px/1.4 ui-monospace,Menlo,monospace;white-space:pre-wrap;word-break:break-word;background:rgba(0,0,0,0.18);padding:6px 8px;border-radius:6px;margin-bottom:8px" id="__hepcure_crash_msg__"></div>' +
        '<button type="button" style="background:white;color:#B4351B;border:none;padding:5px 12px;border-radius:6px;font-weight:800;cursor:pointer;font-size:12px" onclick="document.getElementById(\'__hepcure_crash_banner__\').remove()">Dismiss</button>' +
        "</div>";
      document.body.prepend(el);
      const msgEl = document.getElementById("__hepcure_crash_msg__");
      if (msgEl) msgEl.textContent = String(msg).slice(0, 500);
    } catch (_e) { /* absolute last-ditch — ignore */ }
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
